import React, { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight, ZoomIn, Image as ImageIcon } from 'lucide-react';

const getImageAlt = (imgUrl, title, idx) => {
  const map = {
    'energirude-eksperten-rabat-kampagnebrochure.jpg': 'Energirude Eksperten kampagnetilbud og brochure med 20% rabat',
    'window-replacement-process.jpg': 'Professionel udskiftning og montering af vinduesruder',
    'energirude-eksperten-servicebil-glasstativ.jpg': 'Energirude Ekspertens servicebil med specialbygget glasstativ',
    'energirude-eksperten-firmabil-stiger.jpg': 'Energirude Ekspertens firmabil med stiger klar til montage',
    'modern-danish-home-windows.jpg': 'Moderne bolig med store energieffektive vinduer',
    'parcelhus-energiruder.jpg': 'Klassisk parcelhus med nyligt udskiftede energiruder',
    'panoramavindue-havudsigt-traeterrasse.png': 'Panoramavinduer med havudsigt mod have og træterrasse',
    'energy-efficient-glass-detail.jpg': 'Detalje af moderne isolerende energirude opbygning',
    'moderne-villa-store-energiruder.jpg': 'Moderne arkitekttegnet villa med store glaspartier og energiruder',
    'moderne-erhvervsbyggeri-store-glasfacader.png': 'Moderne erhvervsbygning med energieffektive glasfacader',
    'hvidt-parcelhus-terrasse-glaspartier.jpg': 'Hvidt parcelhus med store glasdøre ud mod terrassen'
  };
  for (const [key, alt] of Object.entries(map)) {
    if (imgUrl && imgUrl.includes(key)) return alt;
  }
  return `${title} - Eksempel ${idx + 1} på glarmesterarbejde`;
};

export default function Gallery({ slug }) {
  const [gallery, setGallery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImageIdx, setActiveImageIdx] = useState(null);

  useEffect(() => {
    async function loadGallery() {
      try {
        setLoading(true);
        // Fetch the galleries collection
        const response = await fetch('https://api.dev.auxo.dk/public/content/galleries');
        if (!response.ok) throw new Error('Failed to fetch galleries');
        const items = await response.json();
        
        // Find the gallery that matches the slug
        const matched = items.find(item => item.slug?.da === slug || item.slug === slug);
        if (matched) {
          setGallery(matched);
        }
      } catch (error) {
        console.error('Error loading gallery:', error);
      } finally {
        setLoading(false);
      }
    }
    loadGallery();
  }, [slug]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-brand-dark/50">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-brand-blue border-t-transparent mb-3" />
        <p className="text-sm">Henter galleri...</p>
      </div>
    );
  }

  if (!gallery || !gallery.images || gallery.images.length === 0) {
    return null; // Return empty if no gallery found
  }

  const handlePrev = (e) => {
    e.stopPropagation();
    setActiveImageIdx((prev) => (prev === 0 ? gallery.images.length - 1 : prev - 1));
  };

  const handleNext = (e) => {
    e.stopPropagation();
    setActiveImageIdx((prev) => (prev === gallery.images.length - 1 ? 0 : prev + 1));
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 border-b border-brand-light pb-3">
        <ImageIcon className="w-5 h-5 text-brand-blue" />
        <h3 className="text-lg font-bold text-brand-deep">Se vores arbejde: {gallery.title}</h3>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        {gallery.images.map((imgUrl, idx) => (
          <button
            key={idx}
            onClick={() => setActiveImageIdx(idx)}
            className="group relative aspect-square rounded-xl overflow-hidden bg-brand-light border border-brand-light hover:border-brand-blue/50 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-brand-blue"
          >
            <img
              src={`https://assets.auxodev.dk/cdn-cgi/image/width=400,height=400,fit=cover,quality=85,format=auto/${imgUrl.replace('https://assets.auxodev.dk/', '')}`}
              alt={getImageAlt(imgUrl, gallery.title, idx)}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-brand-deep/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
              <span className="bg-white/90 p-2.5 rounded-full text-brand-deep shadow-md transform scale-90 group-hover:scale-100 transition-transform duration-300">
                <ZoomIn className="w-5 h-5" />
              </span>
            </div>
          </button>
        ))}
      </div>

      {/* Lightbox Modal */}
      {activeImageIdx !== null && (
        <div
          className="fixed inset-0 z-50 bg-brand-dark/95 backdrop-blur-sm flex items-center justify-center p-4 sm:p-6"
          onClick={() => setActiveImageIdx(null)}
        >
          {/* Close Button */}
          <button
            onClick={() => setActiveImageIdx(null)}
            className="absolute top-4 right-4 z-50 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
            aria-label="Luk visning"
          >
            <X className="w-6 h-6" />
          </button>

          {/* Left Arrow */}
          <button
            onClick={handlePrev}
            className="absolute left-4 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors z-40"
            aria-label="Forrige billede"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>

          {/* Image Container */}
          <div className="relative max-w-5xl max-h-[85vh] flex items-center justify-center select-none">
            <img
              src={`https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=90,format=auto/${gallery.images[activeImageIdx].replace('https://assets.auxodev.dk/', '')}`}
              alt={getImageAlt(gallery.images[activeImageIdx], gallery.title, activeImageIdx)}
              className="max-w-full max-h-[80vh] object-contain rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
            <div className="absolute bottom-[-40px] left-0 right-0 text-center text-white/80 text-sm font-medium">
              Billede {activeImageIdx + 1} af {gallery.images.length}
            </div>
          </div>

          {/* Right Arrow */}
          <button
            onClick={handleNext}
            className="absolute right-4 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors z-40"
            aria-label="Næste billede"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </div>
      )}
    </div>
  );
}
